import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import axios from 'axios';
import '../../styles/client/ClientDashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientQuotes = () => {
  const navigate = useNavigate();
  const { token, logout, isAuthenticated } = useClientAuth();
  
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/client/login');
      return;
    }
    fetchQuotes();
  }, [isAuthenticated]);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/client-portal/quotes`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setQuotes(response.data.quotes);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement devis:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/client/login');
      }
      setLoading(false);
    }
  };

  const getStatusBadge = (statut) => {
    const statusMap = {
      'brouillon': { label: 'Brouillon', class: 'draft' },
      'envoye': { label: 'Envoyé', class: 'sent' },
      'vu_par_client': { label: 'Vu', class: 'viewed' },
      'accepte': { label: 'Accepté', class: 'accepted' },
      'refuse': { label: 'Refusé', class: 'refused' },
      'demande_contact': { label: 'Contact demandé', class: 'contact' },
      'expire': { label: 'Expiré', class: 'expired' }
    };

    const status = statusMap[statut] || { label: statut, class: 'default' };
    return <span className={`status-badge ${status.class}`}>{status.label}</span>;
  };

  const handleLogout = () => {
    logout();
    navigate('/client/login');
  };

  if (loading) {
    return (
      <div className="client-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="client-dashboard-page">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>🌿 NVF - Espace Client</h1>
          <button onClick={handleLogout} className="btn-logout">
            🚪 Déconnexion
          </button>
        </div>
      </header>

      <nav className="dashboard-nav">
        <Link to="/client/dashboard" className="nav-item">
          🏠 Tableau de bord
        </Link>
        <Link to="/client/quotes" className="nav-item active">
          📋 Mes devis
        </Link>
        <Link to="/client/property" className="nav-item">
          🌱 Ma propriété
        </Link>
      </nav>

      <div className="dashboard-container">
        <div className="page-header">
          <h2>📋 Historique de mes devis</h2>
          <p>Consultez tous vos devis et téléchargez les PDFs</p>
        </div>

        {quotes.length === 0 ? (
          <div className="empty-state">
            <p>💭 Aucun devis pour le moment</p>
          </div>
        ) : (
          <div className="quotes-list">
            {quotes.map((quote) => (
              <div key={quote.id} className="quote-card">
                <div className="quote-header-row">
                  <h3>{quote.numero_devis}</h3>
                  {getStatusBadge(quote.statut)}
                </div>
                
                <div className="quote-details">
                  <div className="quote-detail-item">
                    <span className="label">📅 Date:</span>
                    <span className="value">
                      {new Date(quote.date_creation).toLocaleDateString('fr-CA')}
                    </span>
                  </div>
                  
                  <div className="quote-detail-item">
                    <span className="label">💵 Montant:</span>
                    <span className="value">{quote.total_final.toFixed(2)}$</span>
                  </div>
                  
                  <div className="quote-detail-item">
                    <span className="label">🛠️ Services:</span>
                    <span className="value">{quote.items?.length || 0}</span>
                  </div>
                </div>

                <div className="quote-actions">
                  {quote.public_token && (
                    <Link 
                      to={`/client/quote/${quote.public_token}`}
                      className="btn-view"
                    >
                      👁️ Voir le devis
                    </Link>
                  )}
                  
                  <a 
                    href={`${API_URL}/api/erp/quotes/${quote.id}/pdf`}
                    className="btn-download"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📥 PDF
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientQuotes;
